import React, { useState, useEffect, useRef } from 'react'
import { FloppyDisk, Brain, Cpu } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useSettingsStore } from '@/stores/settingsStore'
import { usePermission } from '@/hooks/usePermission'
import { cn } from '@/lib/utils'
import { cardChromeClass, iconChipClass, labelClass, labelStyle } from '@/lib/styleKit'
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard'
import MaskedField from './MaskedField'
import { ReadOnlyBanner, SectionLocked } from './SectionAccess'
import openaiLogo from '@/assets/llm-providers/openai.webp'
import anthropicLogo from '@/assets/llm-providers/anthropic.webp'
import llamaLogo from '@/assets/llm-providers/llama.webp'

type ProviderId = 'openai' | 'anthropic' | 'llama'

interface ProviderDef {
  id: ProviderId
  label: string
  description: string
  logo: string
  // monochrome brand marks need dark-mode invert; colored llama stays
  invertInDark: boolean
}

const providers: ProviderDef[] = [
  { id: 'openai',    label: 'OpenAI',       description: 'GPT-4o, GPT-4.1, GPT-5',          logo: openaiLogo,    invertInDark: true  },
  { id: 'anthropic', label: 'Anthropic',    description: 'Claude Opus 4.6, Sonnet, Haiku',  logo: anthropicLogo, invertInDark: true  },
  { id: 'llama',     label: 'Llama (Local)', description: 'Self-hosted Ollama endpoint',     logo: llamaLogo,     invertInDark: false },
]

// Credential field each provider actually needs. Llama (Ollama) authenticates
// by endpoint only — it has no API key, so it maps to nothing here.
const KEY_FIELD_BY_PROVIDER: Partial<Record<ProviderId, 'openai_api_key' | 'anthropic_api_key'>> = {
  openai: 'openai_api_key',
  anthropic: 'anthropic_api_key',
}

const AIModelsSection: React.FC = () => {
  const { config, llmProviders, llmModelSources, fetchLLMProviders, updateConfig, testLLMKey, revealConfigKey, isSaving } = useSettingsStore()

  // §4-C ai_models.manage: Owner/Admin manage, Manager view-only, Rep/Analyst
  // nothing. PUT /auth/config rejects provider/key writes from the last three
  // regardless — this keeps the UI from offering a control that would 403.
  const { allowed: aiAllowed, canWrite } = usePermission('ai_models', 'manage')

  const [provider, setProvider] = useState<ProviderId>('openai')
  const [model, setModel] = useState('')
  const [llamaEndpoint, setLlamaEndpoint] = useState('')
  // Set only by editing the model / endpoint, never by switching tiles — that is
  // what keeps "Save changes" hidden until there is a real change to save.
  const [dirty, setDirty] = useState(false)
  useUnsavedChangesGuard(dirty)

  useEffect(() => {
    fetchLLMProviders()
  }, [])

  // Seed from config once. Later refetches (saving a key refreshes config) must
  // not snap the visible tile back to the saved provider — switching tiles is a
  // view change now, so local state is deliberately ahead of what's stored.
  const seeded = useRef(false)
  useEffect(() => {
    if (!config || seeded.current) return
    seeded.current = true
    setProvider((config.preferred_llm_provider as ProviderId) || 'openai')
    setModel(config.llm_model || '')
    setLlamaEndpoint(config.llama_endpoint || '')
  }, [config])

  const availableModels = llmProviders?.[provider] || []
  const modelSource = llmModelSources?.[provider]
  // Keep the saved model selectable even if the live list no longer offers it
  // (e.g. key changed, model retired) instead of silently blanking the select.
  const modelOptions = model && !availableModels.includes(model)
    ? [model, ...availableModels]
    : availableModels
  const visibleKeyField = KEY_FIELD_BY_PROVIDER[provider]

  // A newly stored key/endpoint may unlock the live model list, so refresh it.
  const persist = async (data: Record<string, any>, successMessage?: string) => {
    await updateConfig(data, successMessage)
    await fetchLLMProviders()
  }

  // Switching provider tiles is purely a view change — it writes nothing, and an
  // unsaved model pick is dropped rather than carried over (the model lists are
  // provider-specific, so it wouldn't be valid for the new provider anyway).
  const selectProvider = (id: ProviderId) => {
    setProvider(id)
    setModel('')
    setDirty(false)
  }

  const selectModel = (m: string) => {
    setModel(m)
    setDirty(true)
  }

  // Saved together: a model only means something alongside the provider that
  // offers it, so "Save changes" writes the visible tile as the preferred one.
  const saveModelChoice = async () => {
    const data: Record<string, any> = { preferred_llm_provider: provider, llm_model: model }
    if (provider === 'llama') data.llama_endpoint = llamaEndpoint

    // This button only ever writes preferred_llm_provider/llm_model — it never
    // touches the API key field. Selecting a provider that needs a key but has
    // none configured yet still "saves" successfully (the choice IS recorded),
    // but a bare "Settings saved" misleadingly reads as "you're fully set up."
    // Say so explicitly instead, so a blank key field is never silently implied
    // to be fine.
    const keyField = KEY_FIELD_BY_PROVIDER[provider]
    const keyConfigured = !keyField || Boolean((config as any)?.[`${keyField}_set`])
    const successMessage = keyConfigured
      ? undefined
      : `Provider saved — add an API key below to start using ${providers.find(p => p.id === provider)?.label}`

    await persist(data, successMessage)
    setDirty(false)
  }

  if (!aiAllowed) {
    return (
      <SectionLocked title="AI settings are managed by your account owner">
        Your role doesn&apos;t include access to LLM provider configuration or API
        keys. AI features still work — they run on the keys a workspace Owner or
        Admin has set up.
      </SectionLocked>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          AI & Models
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Configure LLM providers for email generation, enrichment, and presentations.
        </p>
      </div>

      {!canWrite && (
        <ReadOnlyBanner>
          You can see which provider this workspace uses, but only a workspace
          Owner or Admin can change it or manage the API keys. Stored keys stay
          hidden — they are never sent to your browser.
        </ReadOnlyBanner>
      )}

      {/* Provider selection */}
      <Card className={cardChromeClass} data-settings-anchor="ai-models-provider">
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipClass}>
              <Brain className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div>
              <CardTitle>LLM Provider</CardTitle>
              <CardDescription>Choose your preferred AI provider</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {providers.map(p => {
              const active = provider === p.id
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectProvider(p.id)}
                  disabled={!canWrite}
                  className={cn(
                    'p-4 rounded-lg border text-left transition-colors duration-150',
                    active
                      ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.08] dark:border-[#5e6ad2]/50 dark:bg-[#5e6ad2]/[0.12]'
                      : 'border-border/60 hover:border-border/80 hover:bg-muted/40 dark:border-white/[0.08] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.03]',
                    !canWrite && 'opacity-60 cursor-not-allowed hover:border-border/60 hover:bg-transparent dark:hover:border-white/[0.08] dark:hover:bg-transparent',
                  )}
                >
                  <div className="h-7 w-7 rounded-md flex items-center justify-center overflow-hidden bg-white dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06]">
                    <img
                      src={p.logo}
                      alt={`${p.label} logo`}
                      className={cn(
                        'h-5 w-5 object-contain',
                        p.invertInDark && 'dark:invert',
                      )}
                    />
                  </div>
                  <p
                    className={cn(
                      'mt-2',
                      active
                        ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
                        : 'text-foreground dark:text-[#f7f8f8]',
                    )}
                    style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    {p.label}
                  </p>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                    style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                  >
                    {p.description}
                  </p>
                </button>
              )
            })}
          </div>

          {modelOptions.length > 0 && (
            <div data-settings-anchor="ai-models-model">
              {/* A real <label>: Select takes an `id` for its combobox trigger,
                  so the visible label owns the accessible name (§56) and no
                  `ariaLabel` is needed to shadow it. */}
              <label htmlFor="ai-models-model-select" className={labelClass} style={labelStyle}>
                Model
              </label>
              <Select
                id="ai-models-model-select"
                value={model}
                onChange={selectModel}
                disabled={!canWrite}
                options={[
                  { value: '', label: 'Select a model' },
                  ...modelOptions.map((m: string) => ({ value: m, label: m })),
                ]}
                placeholder="Select a model"
                className="w-full"
              />
              {modelSource && (
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mt-1.5"
                  style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                >
                  {modelSource === 'live'
                    ? 'Models available to your credentials, fetched live from the provider.'
                    : 'Default model list — save a valid key or endpoint to see the models your account can access.'}
                </p>
              )}
            </div>
          )}

          {provider === 'llama' && (
            <div>
              <Input
                id="ai-models-llama-endpoint"
                label="Ollama Endpoint"
                type="url"
                value={llamaEndpoint}
                onChange={e => { setLlamaEndpoint(e.target.value); setDirty(true) }}
                placeholder="http://localhost:11434"
                disabled={!canWrite}
              />
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-1.5"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                Local Llama connects via this endpoint only — no API key required.
              </p>
            </div>
          )}

          {/* Appears only after a real edit. Switching provider tiles clears the
              pending change, so nothing is saved by browsing between them. */}
          {canWrite && dirty && (
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="sm"
                onClick={saveModelChoice}
                loading={isSaving}
                icon={<FloppyDisk className="w-4 h-4" weight="bold" />}
              >
                Save changes
              </Button>
              <span
                className="text-amber-700 dark:text-amber-300"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                Unsaved changes
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Key — only the credential the selected provider needs. Llama
          (local Ollama) needs none, so the card disappears entirely. */}
      {visibleKeyField && (
        <Card className={cardChromeClass} data-settings-anchor="ai-models-api-key">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <Cpu className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
              </div>
              <div>
                <CardTitle>API Key</CardTitle>
                <CardDescription>Securely stored and encrypted</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {visibleKeyField === 'openai_api_key' && (
              <MaskedField
                label="OpenAI API Key"
                isSet={config?.openai_api_key_set || false}
                last4={config?.openai_api_key_last4}
                placeholder="sk-..."
                onReveal={() => revealConfigKey('openai_api_key')}
                onTest={(val) => testLLMKey('openai', val)}
                testLabel="Test OpenAI"
                onCommit={(val) => persist({ openai_api_key: val })}
                onClear={() => persist({ openai_api_key: '' })}
                readOnly={!canWrite}
                clearTooltip="Remove key now — falls back to the platform key"
                clearDescription="Removing takes effect immediately — there is no separate save. Your OpenAI key is deleted from your account as soon as you confirm, and AI features fall back to the platform key, so they keep working. You can add your own key again at any time."
              />
            )}
            {visibleKeyField === 'anthropic_api_key' && (
              <MaskedField
                label="Anthropic API Key"
                isSet={config?.anthropic_api_key_set || false}
                last4={config?.anthropic_api_key_last4}
                placeholder="sk-ant-..."
                onReveal={() => revealConfigKey('anthropic_api_key')}
                onTest={(val) => testLLMKey('anthropic', val)}
                testLabel="Test Claude"
                onCommit={(val) => persist({ anthropic_api_key: val })}
                onClear={() => persist({ anthropic_api_key: '' })}
                readOnly={!canWrite}
                clearTooltip="Remove key now — falls back to the platform key"
                clearDescription="Removing takes effect immediately — there is no separate save. Your Anthropic key is deleted from your account as soon as you confirm, and AI features fall back to the platform key, so they keep working. You can add your own key again at any time."
              />
            )}
          </CardContent>
        </Card>
      )}

    </div>
  )
}

export default AIModelsSection
